import { isEmpty, isNull, isUndefined } from "lodash";
import { ServiceEnum } from "utils/enums/ServiceEnum";
import { AirTimeRegistrationRequestEntity } from "./AirTimeRegistrationRequestEntity";

export interface SelectOption {
  label?: string;
  value?: string;
}

export interface SearchFormValues {
  created?: Array<Record<string, unknown>>;
  status?: number | string;
  code?: string;
  transRef?: string;
  partner?: string;
  platform?: string;
  user?: string[];
  store?: string;
  type?: number | string;
}

export interface SearchContext {
  staffOptions: SelectOption[];
  userOptions: SelectOption[];
  selectedProviderCode?: string;
  selectedPartnerType: number;
  isStore: boolean;
  storeCode?: string;
  limit: number;
}

export class AirTimeRegistrationQuery {
  static resolveUserRef(
    rawUserRef: string[] | undefined,
    staffOptions: SelectOption[],
    userOptions: SelectOption[],
    selectedPartnerType: number,
    isStore: boolean,
  ): string[] | undefined {
    if (!rawUserRef || rawUserRef.length === 0) return undefined;

    if (isStore) {
      return rawUserRef.filter((ele) =>
        staffOptions?.some((child) => child?.value === ele),
      );
    }
    if (selectedPartnerType === 2 || (selectedPartnerType === 1 && !isStore)) {
      return rawUserRef.filter((ele) =>
        userOptions?.some((child) => child?.value === ele),
      );
    }
    return undefined;
  }

  static sanitize<T extends Record<string, unknown>>(obj: T): T {
    const result = { ...obj };
    Object.keys(result).forEach((key) => {
      const v = result[key];
      if (
        isUndefined(v) ||
        isNull(v) ||
        (typeof v === "string" && isEmpty(v?.trim())) ||
        v === "undefined"
      ) {
        delete result[key];
      }
    });
    return result as T;
  }

  static buildSearchQuery(
    formValues: SearchFormValues,
    context: SearchContext,
    dateFrom: string,
    dateTo: string,
  ): AirTimeRegistrationRequestEntity {
    const finalUserRef = this.resolveUserRef(
      formValues.user,
      context.staffOptions,
      context.userOptions,
      context.selectedPartnerType,
      context.isStore,
    );

    const raw = new AirTimeRegistrationRequestEntity({
      fromDate: dateFrom,
      toDate: dateTo,
      status: String(formValues?.status),
      code: formValues?.code,
      partner: formValues?.partner,
      platform: formValues?.platform,
      service: ServiceEnum.AIR_TIME,
      store: context.storeCode ?? formValues.store,
      type: String(formValues?.type),
      user: finalUserRef?.[0],
      page: 0,
      limit: context?.limit || 10,
      transRef: formValues?.transRef,
    });

    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as AirTimeRegistrationRequestEntity;
  }

  static buildExportQuery(
    formValues: SearchFormValues,
    context: SearchContext,
    dateFrom: string,
    dateTo: string,
  ): AirTimeRegistrationRequestEntity {
    const finalUserRef = this.resolveUserRef(
      formValues.user,
      context.staffOptions,
      context.userOptions,
      context.selectedPartnerType,
      context.isStore,
    );

    const raw = new AirTimeRegistrationRequestEntity({
      fromDate: dateFrom,
      toDate: dateTo,
      status: String(formValues?.status),
      code: formValues?.code,
      partner: formValues?.partner,
      platform: formValues?.platform,
      service: ServiceEnum.AIR_TIME,
      store: context.storeCode ?? formValues.store,
      type: String(formValues?.type),
      user: finalUserRef?.[0],
      transRef: formValues?.transRef,
    });

    // const raw = new AirTimeRegistrationRequestEntity({
    //   fromDate: dateFrom,
    //   toDate: dateTo,
    //   status: formValues.status as number | undefined,
    //   code: formValues.code,
    //   transRef: formValues.transRef,
    //   supplierRefCode: formValues.supplierRefCode,
    //   carrierCodes: formValues.carrierCodes,
    //   phoneNumber: formValues.phoneNumber,
    //   storeCodes: [context.storeCode ?? formValues.storeCode],
    //   userRef: finalUserRef,
    //   partnerCodes: [formValues.partnerCode],
    //   providerCode: context.selectedProviderCode,
    //   codeAirTime: formValues.codeAirTime,
    //   service: ServiceEnum.AIR_TIME,
    // });

    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as AirTimeRegistrationRequestEntity;
  }

  static buildTableChangeQuery(
    prevParams: AirTimeRegistrationRequestEntity,
    formValues: Partial<SearchFormValues>,
    page: number,
    limit: number,
    dateFrom?: string,
    dateTo?: string,
  ): AirTimeRegistrationRequestEntity {
    const formRaw = new AirTimeRegistrationRequestEntity({
      status: String(formValues?.status),
      code: formValues?.code,
      partner: formValues?.partner,
      platform: formValues?.platform,
      service: ServiceEnum.AIR_TIME,
      store: formValues?.store,
      type: String(formValues?.type),
      user: formValues.user?.[0],
      transRef: formValues?.transRef,
    });
    const raw = new AirTimeRegistrationRequestEntity({
      ...prevParams,
      ...formRaw,
      fromDate: dateFrom || prevParams.fromDate,
      toDate: dateTo || prevParams.toDate,
      page,
      limit,
    });
    return this.sanitize(
      raw as unknown as Record<string, unknown>,
    ) as unknown as AirTimeRegistrationRequestEntity;
  }

  static buildRedoQuery(
    fromDate: string,
    toDate: string,
  ): AirTimeRegistrationRequestEntity {
    return new AirTimeRegistrationRequestEntity({
      page: 0,
      limit: 10,
      fromDate,
      toDate,
      service: ServiceEnum.AIR_TIME,
    });
  }
}
